import { isDefined } from "@/utils/function-utils";
import { Comparator } from "@/utils/sort";
import { JsonType } from "@/utils/string-utils";
import { formatArrowDecimalValue } from "@/utils/arrow-type";
import { DataType } from "apache-arrow";
import { Field } from "apache-arrow/schema";

export const concatMultiple = <T>(...arrays: T[][]): T[] => ([] as T[]).concat.apply([], arrays);

export const concatMultipleUnique = <T>(...arrays: T[][]): T[] => {
  const set = new Set<T>();
  arrays.forEach((inner) => inner.forEach((t) => set.add(t)));
  return Array.from(set);
};

/** Epoch milliseconds for an Arrow temporal value, which reaches us as a number or a Date. */
const temporalEpochMillis = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.getTime();
  }
  return null;
};

export const fieldValueToJsonType = (value: unknown, schemaField: Field | undefined): JsonType => {
  if (schemaField != null && typeof value === "number" && DataType.isTimestamp(schemaField.type)) {
    return new Date(value).toISOString();
  }

  // Arrow models a calendar date as Date32/Date64, which `DataType.isTimestamp`
  // does NOT match, and Arrow JS hands both to us as epoch milliseconds at UTC
  // midnight. Left alone they reach the grid as a bare number and render as
  // "1769904000000". Emitting a date-only ISO string also lets the column type
  // inference in `dataframe-column-types.ts` recognize the column as a date, so
  // it gets the calendar icon rather than the numeric one.
  if (schemaField != null && DataType.isDate(schemaField.type)) {
    const epochMillis = temporalEpochMillis(value);
    if (epochMillis != null) {
      return new Date(epochMillis).toISOString().slice(0, 10);
    }
  }

  if (schemaField != null && DataType.isDecimal(schemaField.type)) {
    const decimalValue = formatArrowDecimalValue(value, schemaField.type.scale);
    if (decimalValue != null) {
      return decimalValue;
    }
  }

  return value as JsonType;
};

export type NonEmptyArray<T> = [T, ...T[]];

export type Reducer<Accum, T> = (accum: Accum, element: T) => Accum;

export const isNonEmptyArray = <T>(
  arr: readonly T[] | null | undefined,
): arr is NonEmptyArray<T> => {
  return isDefined(arr) && Array.isArray(arr) && arr.length > 0;
};

export const isNonEmptySet = <T>(setMaybe: Set<T> | null | undefined): setMaybe is Set<T> => {
  return isDefined(setMaybe) && setMaybe instanceof Set && setMaybe.size > 0;
};

/**
 * Creates a comparator for two generic arrays. Arrays should be sorted individually to work!
 * Priority is nullish (nullish last), then length, then the comparison goes to the first element of each
 * with the provided comparator
 *
 * @param individualComparator if arrays provided are the same length, uses a comparison on the first element of each
 */
export const makeCompareArray =
  <T>(individualComparator: (a: T, b: T) => number): Comparator<T[] | null | undefined> =>
  (arrA: T[] | null | undefined, arrB: T[] | null | undefined): number => {
    if (!isNonEmptyArray(arrA)) {
      return -1;
    } else if (!isNonEmptyArray(arrB)) {
      return 1;
    }
    // Note(Kelvin): The order of operations is correct...eslint thinks the parentheses are unnecessary
    return arrA!.length - arrB!.length || individualComparator(arrA![0], arrB![0]);
  };

export const compareStringArrays = makeCompareArray<string>((a, b) => a.localeCompare?.(b) ?? 0);

// factory function that produces a mapper that fills in a nullable array with a default value
export const withDefault =
  <T>(defaultValue: T) =>
  (maybeValue: T | null | undefined): T =>
    maybeValue ?? defaultValue;

export const reduceToSum: Reducer<number, number> = (accum: number, element: number) =>
  accum + element;

export const chunkArray = <T>(array: T[], size: number): T[][] => {
  if (size <= 0) throw new Error("Size must be greater than 0");
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

export const findAndPop = <T>(
  arr: T[],
  condition: (elem: T, index: number, arr: T[]) => boolean,
): T | null => {
  const index = arr.findIndex(condition);
  if (index === -1) {
    return null;
  }

  return arr.splice(index, 1).at(0) ?? null;
};
